import { useState, useCallback, useEffect, useMemo } from 'react';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import localApiAdapter from '../../core/services/localApiAdapter';
import { usePointageEmployeeOptions } from '../pointage/pointageListPageUtils';
import {
  SAUDI_DEBIT_CATEGORIES_FLAT,
  SAUDI_CREDIT_CATEGORIES_FLAT,
} from './saudiTreasuryDefaults';

/** Next entity reference (رمز) for caisse / treasury CRUD pages. */
export function nextCaisseEntityRef(settings, prefixKey, fallback, records, getRef) {
  const prefix = getEntityPrefix(settings, prefixKey, fallback);
  return generateEntityReference(prefix, records, getRef);
}

/** Build column picker defs from { key, labelKey? } + label fn `p`. */
export function buildCaisseColumnDefs(columns, labelFn, columnVisibility = null) {
  return columns.map((col) => ({
    key: col.key,
    label: col.labelKey ? labelFn(col.labelKey) : col.label,
    checked: col.key === 'actions'
      ? true
      : columnVisibility
        ? columnVisibility[col.key] !== false
        : col.checked !== false,
    required: col.required || col.key === 'actions',
  }));
}

/** Column visibility state for caisse list tables. */
export function useCaisseColumnVisibility(columnKeys = []) {
  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(columnKeys.filter((k) => k !== 'actions').map((key) => [key, true])),
  );
  const handleToggleColumn = useCallback((key) => {
    if (key === 'actions') return;
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);
  const isColumnVisible = useCallback(
    (key) => key === 'actions' || columnVisibility[key] !== false,
    [columnVisibility],
  );
  return { columnVisibility, handleToggleColumn, isColumnVisible };
}

/** Visible data columns + optional checkbox + actions for colSpan. */
export function caisseTableColSpan(qawaemColumns, { hasCheckbox = true } = {}) {
  const visible = qawaemColumns.filter((c) => c.key !== 'actions' && c.checked !== false).length;
  return (hasCheckbox ? 1 : 0) + visible + 1;
}

/** Modal tab rules: ≤8 no tabs | 9-17 two tabs | ≥18 two-three tabs */
export function getCaisseModalTabCount(fieldCount) {
  if (fieldCount <= 8) return 0;
  if (fieldCount <= 17) return 2;
  return 3;
}

export function buildCaisseModalFormTabs(allTabs, fieldCount) {
  const tabCount = getCaisseModalTabCount(fieldCount);
  if (tabCount === 0) return [];
  return allTabs.slice(0, tabCount);
}

export const CAISSE_FORM_GRID = 'grid grid-cols-1 md:grid-cols-3 gap-4';

export { useTreasuryConfig, useTreasuryListViewMode } from './useTreasuryConfig';

/** Cap list rows to configured treasury itemsPerPage. */
export function sliceCaisseListItems(items = [], itemsPerPage = 50) {
  const limit = Math.max(1, parseInt(itemsPerPage, 10) || 50);
  return items.slice(0, limit);
}

// ——— Live caisse accounts (transfer source/dest, reconciliation register) ———

export const CAISSE_ACCOUNTS_REFRESH_EVENT = 'qawaem:caisse-accounts-refresh';

export function dispatchCaisseAccountsRefresh() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CAISSE_ACCOUNTS_REFRESH_EVENT));
  }
}

export async function fetchCaisseAccounts(apiGet = localApiAdapter.get.bind(localApiAdapter)) {
  try {
    const dashboard = await apiGet('/tresorerie/position/by-type/caisse');
    return dashboard?.accounts || dashboard?.balanceByCaisse || [];
  } catch (error) {
    console.error('Failed to fetch caisse accounts:', error);
    return [];
  }
}

/** Live caisse register accounts — refreshes when TreasuryPage mutates accounts. */
export function useCaisseAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setAccounts(await fetchCaisseAccounts());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useCaisseRefreshListener(CAISSE_ACCOUNTS_REFRESH_EVENT, refresh);

  return { accounts, loading, refresh };
}

export function resolveCaisseAccountBalance(account) {
  if (!account) return 0;
  return Number(account.balance ?? account.solde ?? 0);
}

export function findCaisseAccountByName(accounts, name) {
  if (!name || !accounts?.length) return null;
  return accounts.find((a) => a.name === name) || null;
}

// ——— HR employee pickers (live /hr/employees + refresh events) ———

/** Live HR employee options for caisse responsible / cashier / witness pickers. */
export function useCaisseEmployeeOptions(options = {}) {
  return usePointageEmployeeOptions(localApiAdapter.get.bind(localApiAdapter), options);
}

export function findCaisseEmployeeMeta(employeeOptions, idOrValue) {
  if (idOrValue == null || idOrValue === '') return null;
  const match = (employeeOptions || []).find(
    (o) => String(o.meta?.id) === String(idOrValue) || String(o.value) === String(idOrValue),
  );
  return match?.meta || null;
}

export function buildCaisseEmployeeSelectOptions(employeeOptions, { emptyLabel = '', valueField = 'id' } = {}) {
  const opts = (employeeOptions || []).map((o) => ({
    value: valueField === 'id' ? String(o.meta?.id ?? o.value) : o.value,
    label: o.label,
    isDefault: true,
    meta: o.meta,
  }));
  return emptyLabel ? [{ value: '', label: emptyLabel }, ...opts] : opts;
}

// ——— Treasury operation types (live /treasury/operation-types) ———

export const CAISSE_OPERATION_TYPES_REFRESH_EVENT = 'qawaem:caisse-operation-types-refresh';

export function dispatchCaisseOperationTypesRefresh() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CAISSE_OPERATION_TYPES_REFRESH_EVENT));
  }
}

function useCaisseRefreshListener(eventName, callback) {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = () => { void callback(); };
    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [eventName, callback]);
}

export function mapTreasuryOperationTypeFromApi(row) {
  const apiCategory = row?.category || 'expense';
  const type = apiCategory === 'revenue' ? 'credit' : 'debit';
  const uiCategory = row?.description || row?.code || row?.name || '';
  return {
    id: row.id,
    code: row.code,
    name: row.name,
    type,
    category: uiCategory,
    description: row.description || '',
    status: row.is_active === 0 || row.is_active === false ? 'inactive' : 'active',
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export function mapTreasuryOperationTypeToApi(form, { editingCode } = {}) {
  const slug = (form.name || 'OP')
    .replace(/[^\w\u0600-\u06FF\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 24)
    .toUpperCase();
  return {
    code: editingCode || form.code || `${slug || 'OP'}-${Date.now().toString(36).slice(-4)}`,
    name: form.name,
    category: form.type === 'credit' ? 'revenue' : 'expense',
    module: 'treasury',
    description: form.category || form.description || '',
    is_active: form.status === 'inactive' ? 0 : 1,
    parent_id: null,
  };
}

export async function fetchTreasuryOperationTypes(apiGet = localApiAdapter.get.bind(localApiAdapter)) {
  try {
    const response = await apiGet('/treasury/operation-types', { module: 'treasury' });
    const rows = Array.isArray(response) ? response : (response?.data || []);
    return rows.map(mapTreasuryOperationTypeFromApi);
  } catch (error) {
    console.error('Failed to fetch treasury operation types:', error);
    return [];
  }
}

export function buildDefaultOperationCategories() {
  const debit = SAUDI_DEBIT_CATEGORIES_FLAT.map((c) => ({
    category: c.name,
    operations: [...(c.operations || [])],
  }));
  const credit = SAUDI_CREDIT_CATEGORIES_FLAT.map((c) => ({
    category: c.name,
    operations: [...(c.operations || [])],
  }));
  return {
    operationCategories: { debit, credit },
    debitTypes: debit.map((c) => c.category),
    creditTypes: credit.map((c) => c.category),
    operations: [],
  };
}

/** Group API operation types into journal picker shape { debit, credit } + flat type lists. */
export function buildOperationCategoriesFromTypes(apiTypes = []) {
  if (!apiTypes.length) return buildDefaultOperationCategories();

  const debitBuckets = {};
  const creditBuckets = {};

  apiTypes
    .filter((row) => row.status !== 'inactive')
    .forEach((row) => {
      const uiCategory = row.category || row.description || row.name;
      const bucket = row.type === 'credit' ? creditBuckets : debitBuckets;
      if (!bucket[uiCategory]) bucket[uiCategory] = [];
      if (row.name && !bucket[uiCategory].includes(row.name)) {
        bucket[uiCategory].push(row.name);
      }
    });

  const debit = Object.entries(debitBuckets).map(([category, operations]) => ({ category, operations }));
  const credit = Object.entries(creditBuckets).map(([category, operations]) => ({ category, operations }));

  if (!debit.length && !credit.length) return buildDefaultOperationCategories();

  return {
    operationCategories: { debit, credit },
    debitTypes: debit.map((c) => c.category),
    creditTypes: credit.map((c) => c.category),
    operations: apiTypes,
  };
}

/** Live treasury operation types — refreshes when OperationTypesPage mutates. */
export function useCaisseOperationTypes() {
  const [apiTypes, setApiTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setApiTypes(await fetchTreasuryOperationTypes());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useCaisseRefreshListener(CAISSE_OPERATION_TYPES_REFRESH_EVENT, refresh);

  const grouped = useMemo(() => buildOperationCategoriesFromTypes(apiTypes), [apiTypes]);

  return {
    apiTypes,
    operations: grouped.operations,
    operationCategories: grouped.operationCategories,
    debitTypes: grouped.debitTypes,
    creditTypes: grouped.creditTypes,
    loading,
    refresh,
  };
}
